import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckIcon, PhoneIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnPrimary, btnSecondary } from '../ui/styles';
import { useAdmin } from '../AdminContext';
import { useCan } from '../useCan';
import { arriveAgendaAppointment, fetchAgenda, fetchStaffCustomer } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { ownOnly } from '../../core/rules/staffAccess';
import { courseWarning } from '../../core/rules/staffPermissions';
import { staffList } from '../../core/rules/salonDays';
import { errorMessage } from '../../core/errors';
import { isAbortError } from '../../core/api/http';
import { today } from '../../core/format/dates';
import { ADMIN_BASE } from '../../utils/hosts';
import type { AgendaAppointment } from '../../core/types/agenda';
import type { StaffCustomerCard } from '../../core/types/adminRequests';
import type { RequestOptions } from '../../core/types/backend';
const nowTime = () => new Date().toTimeString().slice(0, 5);

/**
 * Mijn dag: telefoon eerst. De volgende klant bovenaan met wat je nodig hebt (huidplan,
 * aandachtspunten, vorige notitie) en de drie handelingen van de dag: binnen, afronden, afrekenen.
 * Daaronder de rest van de dag. Een collega ziet alleen haar eigen afspraken.
 */
export function MyDay() {
  const viewer = useCan().viewer('agenda.view');
  const load = useCallback((opts: RequestOptions) => {
    const d = today();
    return fetchAgenda(d, d, opts);
  }, []);
  const {
    data,
    setData,
    status,
    reload
  } = useLatestLoad(load);
  const day = today();
  const list = ownOnly(data?.appointments ?? [], viewer).filter(a => a.date === day && a.status === 'bevestigd' && a.outcome !== 'geannuleerd').sort((a, b) => a.time.localeCompare(b.time));
  // De volgende: de eerste die nog niet is afgerekend en niet "niet gekomen" is.
  const next = list.find(a => !a.saleId && a.outcome !== 'no-show' && !(a.outcome === 'afgerond' && a.kind !== 'salon'));
  const rest = list.filter(a => a !== next);
  const replace = (a: AgendaAppointment) => setData(r => r ? {
    ...r,
    appointments: r.appointments.map(x => x.id === a.id ? a : x)
  } : r);
  return <div className="mx-auto max-w-xl" data-id="element-3261" data-mp="z9d28fm58wu9">
      <PageHeader title="Mijn dag" description={`${list.length} ${list.length === 1 ? 'afspraak' : 'afspraken'} vandaag`} data-id="element-3262" data-mp="bj8wa1lhgl03" />
      {status === 'error' && !data ? <LoadFailed title="De agenda laden lukte niet" onRetry={reload} data-id="element-3263" data-mp="7jglrjcj44s3" /> : !data ? <LoadingLine label="Je dag laden…" data-id="element-3264" data-mp="lizrk5hvb418" /> : !next ? <p className="rounded-lg border border-line bg-white px-5 py-8 text-center text-[14px] text-muted" data-id="element-3265" data-mp="gi2ci1s5mq3y">
          {list.length ? 'Alles is afgerond en afgerekend.' : 'Geen afspraken vandaag.'}
        </p> : <NextClient key={next.id} a={next} onChange={replace} data-id="element-3266" data-mp="1rwpdvuqrnk8s" />}

      {rest.length > 0 && <section aria-labelledby="rest" className="mt-8" data-id="element-3267" data-mp="39tkdau796kk0">
          <h2 id="rest" className="text-[15px] font-medium text-ink" data-id="element-3268" data-mp="3euq7kwgkafvn">
            De rest van de dag
          </h2>
          <ul className="mt-2 divide-y divide-line border-y border-line" data-id="element-3269" data-mp="30v7e99set93e">
            {rest.map(a => <DayRow key={a.id} a={a} data-id="element-3270" data-mp="35ujx7jg5tw0p" />)}
          </ul>
        </section>}
    </div>;
}
function NextClient({
  a,
  onChange
}: {
  a: AgendaAppointment;
  onChange: (a: AgendaAppointment) => void;
}) {
  const {
    index,
    site
  } = useAdmin();
  const team = staffList(site.staff);
  const [card, setCard] = useState<StaffCustomerCard | null>(null);
  const [cardFailed, setCardFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const treatment = index.treatmentBySlug(a.treatmentSlug)?.name ?? a.treatmentSlug;
  const started = a.time <= nowTime();
  const finished = a.outcome === 'afgerond';
  const warning = a.session ? courseWarning(a.customer.kuur?.staffId, a.staffId, team) : '';
  useEffect(() => {
    if (!a.customer.id) return;
    const controller = new AbortController();
    fetchStaffCustomer(a.customer.id, {
      signal: controller.signal
    }).then(setCard).catch((err: unknown) => {
      if (!isAbortError(err)) setCardFailed(true);
    });
    return () => controller.abort();
  }, [a.customer.id]);
  const arrive = async () => {
    setBusy(true);
    try {
      onChange(await arriveAgendaAppointment(a.id));
    } catch (err) {
      toast.error('Opslaan lukte niet', {
        description: errorMessage(err)
      });
    } finally {
      setBusy(false);
    }
  };
  const lastNote = card?.treatments.find(t => t.note);
  return <article aria-labelledby="volgende" className="rounded-lg border border-line bg-white" data-id="element-3271" data-mp="2xmh7bf0f889t">
      <div className="border-b border-line px-5 py-4" data-id="element-3272" data-mp="3v137freuvsyn">
        <p className="text-[13px] text-muted" data-id="element-3273" data-mp="3v2nznadp6ztc">
          {finished ? 'Nog afrekenen' : a.arrivedAt ? 'Binnen' : started ? 'Nu' : 'Volgende'} · {a.time}
        </p>
        <h2 id="volgende" className="mt-0.5 font-display text-[1.7rem] font-light leading-tight text-ink" data-id="element-3274" data-mp="1h6pfsw8fwt6">
          {a.customer.name}
        </h2>
        <p className="text-[14px] text-ink" data-id="element-3275" data-mp="6hknci44dqdh">
          {treatment}
          {a.session ? ` · sessie ${a.session}` : ''}
          {a.customer.firstVisit ? ' · eerste bezoek' : ''}
        </p>
        {warning && <p className="mt-2 rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-3276" data-mp="fdtl7scg8gxi">{warning}</p>}
      </div>

      <div className="space-y-4 px-5 py-4 text-[14px]" data-id="element-3277" data-mp="kdetitdie1mw">
        {card?.attention && card.attention.length > 0 && <section aria-label="Let op" className="rounded-md bg-blush/60 px-3 py-2.5" data-id="element-3278" data-mp="u5axgl2a0lz1">
            <h3 className="text-[13px] font-medium text-ink" data-id="element-3279" data-mp="z45m43mxd4r2">Let op</h3>
            <ul className="mt-1 list-disc pl-4 text-[13px] text-ink" data-id="element-3280" data-mp="z3cgj5zpclai">
              {card.attention.map(x => <li key={x} data-id="element-3281" data-mp="133way1sw5asy">{x}</li>)}
            </ul>
          </section>}
        {card?.plan && <section aria-label="Huidplan" data-id="element-3282" data-mp="347oyuin0104l">
            <h3 className="text-[13px] text-muted" data-id="element-3283" data-mp="348hykznjipd8">Huidplan</h3>
            {card.plan.focus && <p className="text-ink" data-id="element-3284" data-mp="307eneqgfthic">{card.plan.focus}</p>}
            {card.plan.note && <p className="mt-0.5 whitespace-pre-line text-[13px] text-muted" data-id="element-3285" data-mp="3dybda9izbklp">{card.plan.note}</p>}
          </section>}
        {lastNote && <section aria-label="Vorige notitie" data-id="element-3286" data-mp="395qgr08outeh">
            <h3 className="text-[13px] text-muted" data-id="element-3287" data-mp="3nvfwq1lfx1yt">Vorige keer ({index.treatmentBySlug(lastNote.treatmentSlug)?.name ?? lastNote.treatmentSlug})</h3>
            <p className="whitespace-pre-line text-[13px] leading-relaxed text-ink" data-id="element-3288" data-mp="3iwl3s6trdx0z">{lastNote.note}</p>
          </section>}
        {a.customer.id && !card && !cardFailed && <p role="status" className="text-[13px] text-muted" data-id="element-3289" data-mp="3ry2ibkripqnk">
            Klantkaart laden…
          </p>}
        {!a.customer.id && <p className="text-[13px] text-muted" data-id="element-3290" data-mp="1xjjgzlippt74">Gast zonder account: geen huidplan of eerdere notities.</p>}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-line px-5 py-4" data-id="element-3291" data-mp="d1r0b7ah3dls">
        {!a.arrivedAt && !finished && <button type="button" onClick={() => void arrive()} disabled={busy} aria-busy={busy} className={started ? btnPrimary : btnSecondary} data-id="element-3292" data-mp="i0u1ggnc0zoh">
            <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3293" data-mp="472y5q072c17" />
            Binnen
          </button>}
        {!finished && <Link to={`${ADMIN_BASE}/agenda?afspraak=${encodeURIComponent(a.id)}`} className={a.arrivedAt ? btnPrimary : btnSecondary} data-id="element-3294" data-mp="3qfcbup0qwrb9">
            Afronden
          </Link>}
        <Link to={`${ADMIN_BASE}/kassa?afspraak=${encodeURIComponent(a.id)}`} className={finished ? btnPrimary : btnSecondary} data-id="element-3295" data-mp="3hg7dbip6oxz3">
          Afrekenen
        </Link>
        {a.customer.phone && <a href={`tel:${a.customer.phone.replace(/\s/g, '')}`} className={btnSecondary} aria-label={`Bel ${a.customer.name}`} data-id="element-3296" data-mp="1fnsyishxs9mw">
            <PhoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3297" data-mp="1jommtmzajqgb" />
          </a>}
        {a.customer.id && <Link to={`${ADMIN_BASE}/klanten/${a.customer.id}`} className="ml-auto self-center text-[13px] text-muted underline-offset-4 hover:underline" data-id="element-3298" data-mp="7incr3tdvn0g">
            Klantkaart
          </Link>}
      </div>
    </article>;
}
function DayRow({
  a
}: {
  a: AgendaAppointment;
}) {
  const {
    index
  } = useAdmin();
  const label = a.saleId ? 'Afgerekend' : a.outcome === 'no-show' ? 'Niet gekomen' : a.outcome === 'afgerond' ? 'Nog afrekenen' : null;
  return <li data-id="element-3299" data-mp="3fvcnwhyijwp9">
      <Link to={`${ADMIN_BASE}/agenda?afspraak=${encodeURIComponent(a.id)}`} className="flex items-center gap-3 py-3" data-id="element-3300" data-mp="3fut1anxx4k4w">
        <span className="w-12 shrink-0 text-[14px] tabular-nums text-muted" data-id="element-3301" data-mp="3azdpe1jh0yb4">{a.time}</span>
        <span className="min-w-0 flex-1" data-id="element-3302" data-mp="36014hmc5u1b6">
          <span className="block truncate text-[14px] text-ink" data-id="element-3303" data-mp="195o6tbis327t">{a.customer.name}</span>
          <span className="block truncate text-[13px] text-muted" data-id="element-3304" data-mp="146toeaoq3fo8">{index.treatmentBySlug(a.treatmentSlug)?.name ?? a.treatmentSlug}</span>
        </span>
        {label && <span className="shrink-0 rounded-full bg-sand px-2.5 py-1 text-[12px] text-ink" data-id="element-3305" data-mp="1cnc61r64x0qd">{label}</span>}
      </Link>
    </li>;
}